import React, { useRef, useEffect, useState } from "react";
import styles from "../../assets/stylesheets/Assistant.module.scss";
import { getAssistantInfo } from "../../utils/api/endpoints/business/assistant";
import usePageTitle from "../../utils/hooks/usePageTitle";
import { MarkdownViewer } from "../../utils/sanitizers";
import { FaSpinner } from "react-icons/fa";
import { AiOutlineSend } from "react-icons/ai";

const ChatAssistant = () => {
  const [chat, setChat] = useState<{ text: string; isUser: boolean }[]>([]);
  const [question, setQuestion] = useState("");
  const [threadId, setThreadId] = useState("");
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false); // Add isTyping state
  const [languageUsed, setLanguageUsed] = useState<string>("auto");

  const chatContainerRef = useRef<HTMLDivElement | null>(null);

  const addMessageToChat = (text: string, isUser = false) => {
    setChat(prevChat => [...prevChat, { text, isUser }]);
  };

  const generateResponse = async () => {
    if (question.trim() !== "") {
      setLoading(true);
      setIsTyping(true); // Set isTyping to true
      addMessageToChat(question, true);
      setQuestion("");

      const response = await getAssistantInfo(question, threadId);
      setIsTyping(false); // Set isTyping to false
      addMessageToChat(response.answer);
      setThreadId(response.threadId);
      setLanguageUsed(response.languageUsed);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [chat]);

  usePageTitle("Chat Assistant");

  return (
    <section className={styles.container}>
      <div className={styles.chatContainer} ref={chatContainerRef}>
        {chat.map((message, index) => (
          <div
            key={index}
            className={
              message.isUser ? styles.userMessage : styles.assistantMessage
            }
          >
            {message.isUser ? (
              <div className={styles.bubbleUser}>{message.text}</div>
            ) : (
              <div className={styles.bubbleAssistant}>
                <MarkdownViewer
                  data={message.text}
                  languageUsed={languageUsed}
                />
              </div>
            )}
          </div>
        ))}
        {isTyping && ( // Display typing bubble if isTyping is true
          <div className={styles.assistantMessage}>
            <div className={styles.typingBubble}></div>
          </div>
        )}
      </div>

      <div className={styles.typeSection}>
        <div className={styles.inputWrapper}>
          <textarea
            // type="text"
            rows={1}
            value={question}
            className={styles.questionInpt}
            onChange={e => setQuestion(e.target.value)}
            placeholder="Hello! How can I assist you with your cybersecurity needs today?"
            onKeyDown={e => {
              if (e.key === "Enter")
                document.getElementById("submitButtonRef")?.click();
            }}
          />
          <button
            className={styles.getAnsBtn}
            disabled={question.trim() === "" || loading}
            onClick={generateResponse}
            id={"submitButtonRef"}
          >
            {loading ? (
              <FaSpinner className={`${styles.spinner} ${styles.spin}`} />
            ) : (
              <AiOutlineSend />
            )}
          </button>
        </div>

        <pre className={styles.moreInfotext}>
          If you've got more questions, you can always reach out to us at{" "}
          <a href="mailto://support@capturethebug.xyz">
            support@capturethebug.xyz
          </a>
        </pre>
      </div>
    </section>
  );
};

export default ChatAssistant;
